// import { baseDiag } from "../../utils/fsDiag"; // (the settle-wait diag, removed)
import TickerTimeout from "../../utils/TickerTimeout";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { anticipationChain } from "./scatterAnticipation";
// The anticipation's fire sound moved to the board FRAME (2026-10-20, Vladimir:
// "when fire around reel starts, not … each individual anticipation per reel")
// — GameViewSimpleController play/stop/hideReelAnticipation. Was here:
// import { soundManager } from "../../SoundManager";
// import { SOUND_NAME } from "../../sounds";
// const startFireSound = () => {
//   soundManager.stop(SOUND_NAME.anticipation_fire);
//   soundManager.play(SOUND_NAME.anticipation_fire);
// };
// const stopFireSound = () => soundManager.stop(SOUND_NAME.anticipation_fire);
// import { isTurboLike } from "../../utils/speedHelpers";

/**
 * createAnticipationRunner — the per-reel anticipation chain, for ANY room.
 *
 * Cleopatra's Fire (GDD §6.1 scatters, §6.2 coins, base game AND free spins):
 * the board is known before a reel stops, so anticipationChain() names the
 * chain reels up front, and the ANIMATION then drives the chain one reel at a
 * time:
 *
 *   reel before the chain lands  -> chain reel 1: in -> loop (x LOOPS)
 *   ...its in + loops elapse     -> chain reel 1 stops
 *   chain reel 1 lands           -> out, in full
 *   out finishes                 -> chain reel 2: in -> loop ...
 *   last chain reel's out ends   -> any reels after the chain stop
 *
 * Every reel after the chain's start is HELD by the chain — the room must not
 * schedule its stop (see `holds`). The reels still waiting their turn are
 * dimmed. A press skips the reel on its turn (see `skipActiveReel`).
 *
 * Timing runs on TickerTimeouts from the clips' own durations, never on spine
 * listeners, so a missing spine can slow the chain but never leave a reel
 * spinning.
 *
 * The chain STATE lives in `chainRef`, keyed by round, so a runner can be
 * built fresh wherever it is needed (a stop, a re-entry, a press) and every
 * one of them sees the same chain.
 *
 * @param {object}   deps
 * @param {{current:object|null}} deps.chainRef  The room's chain state ref.
 * @param {() => Array}  deps.getReels   The room's reel refs (getReelController,
 *                                       stopSpin).
 * @param {() => object} deps.getScene   The room's scene controller
 *                                       (playAnticipation, stopAnticipation,
 *                                       hideAnticipation, getAnticipationTiming).
 * @param {() => object} [deps.getDim]   The room's DimReelSet (dimReel,
 *                                       undimReel) — the waiting reels' dim.
 * @param {(reelIndex:number) => void} deps.stopReel  Stop one reel now, with
 *                                       the room's own bookkeeping.
 * @param {(t, reelIndex:number|null) => void} deps.track    Register a timeout
 *                                       for the room's cleanup.
 * @param {(t, reelIndex:number|null) => void} deps.untrack  Drop it again.
 * @param {number} [deps.normalGapMs]    Gap between the reels after the chain.
 */
// The coin counter comes this long after the board fire starts (its glow
// peaks at 0.4 s).
const COIN_COUNTER_AFTER_FIRE_MS = 400;

export const createAnticipationRunner = ({
  chainRef,
  getReels,
  getScene,
  getDim,
  // ── Tension dim (base game first, Vladimir 2026-09-29) ──
  // When set, the chain dims EVERY reel but the one anticipating, and the
  // stopped reels are stripped to their trigger symbols (stripReel) — they
  // glow above the dim, everything else is a static picture under it. At the
  // end: a miss lifts the dims and brings the idles back (onChainEnd(false)),
  // a hit hands over to the full board dim (onChainEnd(true)).
  tensionDim = false,
  stripReel,
  onChainEnd,
  // Resolves when a reel's landing SPINES have started (whenReelLanding) —
  // the dim goes on at that moment, under them — and when they have finished
  // (whenReelSettled) — only then is that reel stripped to its triggers.
  whenReelLanding,
  whenReelSettled,
  // A new round: whatever the tension did to the last board's spines is
  // undone at once (tensionSymbols.reset).
  onRoundReset,
  // The chain's first reel starts its turn: `fromReel` is the reel that
  // started it (tensionSymbols.arm — the landing gate).
  onChainStart,
  // ── Without the tension dim (Vladimir, 2026-09-30) ── the stopped reels'
  // trigger symbols play their wait clips (tensionSymbols in waits-only
  // mode): called for every reel already down as each chain reel starts its
  // turn, once the reel before it has started its landing. onChainStart and
  // onChainEnd are called on this path too.
  waitReel,
  stopReel,
  track,
  untrack,
  normalGapMs = FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO,
}) => {
  const cfg = FEATURES_CONFIG.SCATTER_ANTICIPATION ?? {};
  const reels = () => getReels() || [];
  const reelController = (i) => reels()[i]?.getReelController?.();

  const timing = () => {
    const t = getScene()?.getAnticipationTiming?.() ?? {};
    return {
      inMs: t.inMs ?? 667,
      loopMs: t.loopMs ?? 1000,
      outMs: t.outMs ?? 667,
    };
  };
  const loops = cfg.LOOPS ?? 1;

  // The room's DimReelSet, one black column per reel — the win dim's opacity
  // and fades (Vladimir, 2026-09-29: "match them"). `instant` snaps it off.
  // Was a tint on the reel's own sprites, cut in and out with no fade:
  // const setDim = (i, dimmed) =>
  //   reelController(i)?.setDimmed?.(dimmed, cfg.DIM_TINT);
  // The tension dim fades at the WIN presentation's speed (DimReelSet
  // FAST, 50 ms) — 250 ms, after a 250 ms wait, read as slow (Vladimir,
  // 2026-09-29).
  const setDim = (i, dimmed, instant = false) => {
    const dim = getDim?.();
    if (dimmed) dim?.dimReel?.(i, tensionDim);
    else dim?.undimReel?.(i, instant, tensionDim);
  };

  /**
   * Make sure the chain belongs to THIS round. A new round clears whatever the
   * last one left on screen and computes its own chain.
   */
  const sync = (round, roundKey) => {
    if (!chainRef.current || chainRef.current.roundKey !== roundKey) {
      getScene()?.hideAnticipation?.();
      // stopFireSound();
      // getScene()?.hideReelAnticipation?.();
      // ...except the Random Pot's presentation board: its board fire was
      // started BEFORE its answer, with the Pot (ReelControlContext), and is
      // kept — it goes with the last reel's landing (onLandingPeak).
      if (!round?.randomPotPresentation) getScene()?.hideReelAnticipation?.();
      // Its darkened background too (lightenBackground is a no-op if not dark).
      if (!round?.randomPotPresentation) getScene()?.lightenBackground?.();
      // The last board's coin counter goes at once.
      getScene()?.hideCoinCounter?.(true);
      reels().forEach((_, i) => setDim(i, false, true));
      onRoundReset?.();
      const found = anticipationChain(round);
      // chainRef.current = found
      //   ? { roundKey, ...found, step: -1, done: false, stops: new Map() }
      //   : { roundKey, done: true, stops: new Map() };
      chainRef.current = found
        ? {
            roundKey,
            ...found,
            step: -1,
            done: false,
            stops: new Map(),
            // The Random Pot's presentation board: its background is dark
            // until the last reel lands (onLandingPeak).
            presentationDark: !!round?.randomPotPresentation,
          }
        : {
            roundKey,
            done: true,
            stops: new Map(),
            // The Random Pot's presentation board: no chain, but its board
            // fire is up (see above) until the last reel lands.
            presentationFire: !!round?.randomPotPresentation,
            presentationDark: !!round?.randomPotPresentation,
          };
    }
    return chainRef.current;
  };

  /** True when the chain, not the room, stops `index`. */
  const holds = (index) => {
    const chain = chainRef.current;
    return (
      !!chain &&
      !chain.done &&
      typeof chain.fromReel === "number" &&
      index > chain.fromReel
    );
  };

  /** A chain reel is on its turn (in / loop / out). */
  const isActive = () => {
    const chain = chainRef.current;
    return !!chain && !chain.done && chain.step >= 0;
  };

  // Stop a reel the chain owns. The reel on its turn plays its out FROM THE
  // STOP, fading while it drops — not from its landing peak, which left the
  // out (and the next reel) waiting on a reel already down (Vladimir,
  // 2026-09-29: "reel stops, lands, and only then we play out").
  const stopChainReel = (chain, reelIndex) => {
    stopReel(reelIndex);
    if (chain.step >= 0 && reelIndex === chain.reels[chain.step]) {
      endStep(chain, chain.step);
    }
  };

  // A reel stop owned by the chain. Dropped if the round has moved on.
  const scheduleStop = (chain, reelIndex, delayMs) => {
    const timeoutId = new TickerTimeout(() => {
      chain.stops.delete(reelIndex);
      untrack(timeoutId, reelIndex);
      if (chainRef.current !== chain) return;
      stopChainReel(chain, reelIndex);
    }, delayMs);
    chain.stops.set(reelIndex, timeoutId);
    track(timeoutId, reelIndex);
  };

  // The reels still waiting their turn are dimmed; the one anticipating, and
  // every reel already down, are not. `activeReel` null = chain over.
  // Undimming is immediate; dimming waits DIM_DELAY_MS so the waiting reels
  // darken once the anticipation has visibly developed (Vladimir,
  // 2026-09-28). Dropped if the chain has moved on meanwhile.
  const dimWaitingReels = (chain, activeReel) => {
    if (!cfg.DIM_REELS) return;
    if (tensionDim) {
      dimTension(chain, activeReel);
      return;
    }
    const waiting = [];
    reels().forEach((_, i) => {
      if (activeReel !== null && i > activeReel) waiting.push(i);
      else setDim(i, false);
    });
    if (waiting.length === 0) return;

    const step = chain.step;
    const timeoutId = new TickerTimeout(() => {
      untrack(timeoutId, null);
      if (chainRef.current !== chain || chain.done || chain.step !== step) {
        return;
      }
      waiting.forEach((i) => setDim(i, true));
    }, cfg.DIM_DELAY_MS ?? 0);
    track(timeoutId, null);
  };

  // ── The tension dim ── every reel but the anticipating one, after
  // DIM_DELAY_MS; the reels already down lose every animation but their
  // trigger symbols'. `activeReel` null = chain over: a hit crossfades into
  // the full board dim (the win presentation's), a miss lifts the dims and
  // hands the idles back.
  const dimTension = (chain, activeReel) => {
    if (activeReel === null) {
      if (chain.hits) getDim?.()?.dimReels?.(true); // the win's own fast fade
      reels().forEach((_, i) => setDim(i, false));
      onChainEnd?.(!!chain.hits);
      return;
    }
    setDim(activeReel, false);
    // // At once — no DIM_DELAY_MS wait (that was the old waiting-reel dim's).
    // // A reel still spinning dims now; a reel already DOWN waits for its
    // // landing to play out first (Vladimir, 2026-09-29: the dim covered the
    // // last reel's landing and uncovered it later — "that cannot happen").
    // const step = chain.step;
    // reels().forEach((_, i) => {
    //   if (i === activeReel) return;
    //   if (i > activeReel) {
    //     setDim(i, true);
    //     return;
    //   }
    //   baseDiag("tension dim — waiting for reel to settle", { reel: i, activeReel, step });
    //   Promise.resolve(whenReelSettled?.(i)).then(() => {
    //     const stale = chainRef.current !== chain || chain.dimLifted || chain.step !== step;
    //     baseDiag("tension dim — reel settled", { reel: i, step, nowStep: chain.step, dimmed: !stale });
    //     if (stale) return;
    //     setDim(i, true);
    //     stripReel?.(i);
    //   });
    // });
    // ── ONE wait: the reel just before the anticipating one ── it has only
    // just landed; every reel before it settled long ago. When its landing
    // has played out (its idle is up — the landing-complete event, with
    // waitForBoardToSettle's stall guard), every other reel dims at once, and
    // the stopped ones keep only their trigger symbols animated (Vladimir,
    // 2026-09-29: "just wait for 1 reel before anticipation").
    // ── The moment: the reel before the anticipating one starts its landing
    // SPINES ── the dim goes on then, UNDER them (Vladimir, 2026-09-29: "WHEN
    // LANDING ANIMATION STARTS, WE IMMEDIATELY DO DIM"). That reel keeps its
    // landing until the spines finish — only then does it lose its
    // non-trigger animations; the reels before it are stripped at once.
    const step = chain.step;
    const prevReel = activeReel - 1;
    const stale = () =>
      chainRef.current !== chain || chain.dimLifted || chain.step !== step;
    Promise.resolve(prevReel >= 0 ? whenReelLanding?.(prevReel) : null).then(() => {
      if (stale()) return;
      // Every stopped reel — the one just landing included — goes to its
      // pictures NOW; only the trigger symbols (coins, scatters) keep their
      // spines, and the dim sits under them. Nothing else waits on a landing
      // (Vladimir, 2026-09-29: "you wait for scatter to enter idle, not other
      // symbols").
      reels().forEach((_, i) => {
        if (i === activeReel) return;
        setDim(i, true);
        // if (i < prevReel) stripReel?.(i);
        if (i < activeReel) stripReel?.(i);
      });
      // if (prevReel < 0) return;
      // Promise.resolve(whenReelSettled?.(prevReel)).then(() => {
      //   if (!stale()) stripReel?.(prevReel);
      // });
    });
  };

  // // ── Turbo / super turbo play the anticipation at AUTO speed ──
  // // Zeus's rule for its own anticipation (ReelControlContext, "Client
  // // requires uniform anticipation visual"): the held reels leave turbo speed
  // // and spin — and land — as AUTO does, so the chain looks the same at every
  // // speed (Vladimir, 2026-10-01: "why doesnt it play normally"). Per spin:
  // // lockedSpinSpeed resets on the next start and at the landing.
  // const toAutoSpeed = (i) => {
  //   const ctrl = reelController(i);
  //   if (!ctrl || ctrl.hasStopped || !isTurboLike(ctrl.lockedSpinSpeed)) return;
  //   ctrl.lockedSpinSpeed = "auto";
  //   ctrl._speedConfigCache = null;
  //   const speedConfig = ctrl._getSpeedConfig?.();
  //   if (speedConfig?.SPEED_MS_PER_SYMBOL_HEIGHT) {
  //     const safeSpeedMs = Math.max(speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT, 17);
  //     ctrl.normalizedSpinSpeed = (ctrl.symbolHeight / safeSpeedMs) * 1000;
  //   }
  // };

  // Chain reel `step`: in -> loop on it, and its stop after in + loops.
  const startStep = (chain, step) => {
    if (chainRef.current !== chain || chain.done) return;
    chain.step = step;
    const reelIndex = chain.reels[step];
    // // Every reel the chain holds (all after its start) — at its first turn.
    // if (step === 0) {
    //   for (let i = chain.fromReel + 1; i < reels().length; i++) toAutoSpeed(i);
    // }
    // ── NO speed change (Vladimir, 2026-10-01: "we completely miss the spin
    // and produce blank space") ── re-speeding a reel mid-spin tore its strip.
    // Turbo / super turbo reels keep their own speed; the chain only holds
    // them longer and stops them one by one.
    const { inMs, loopMs } = timing();
    // ── A PLAIN turn (CCO demo feedback, 2026-10-13) ── a reel no rule
    // anticipates (a scatter cannot land on it): it stops at the normal gap,
    // naturally — no anticipation, no dim, no frame — and the next turn
    // starts at its stop (endStep).
    if (chain.plain?.includes(reelIndex)) {
      scheduleStop(chain, reelIndex, normalGapMs);
      return;
    }
    // if (step === 0 && tensionDim) onChainStart?.(chain.fromReel);
    // if (step === 0 && tensionDim) onChainStart?.(chain.fromReel, chain.features);
    // if (step === 0 && (tensionDim || waitReel)) {
    // The chain's FIRST anticipated turn (step 0, or the one after the
    // plain turns before it) starts the waits and the board frame.
    const firstLive = !chain.liveStarted;
    chain.liveStarted = true;
    if (firstLive && (tensionDim || waitReel)) {
      onChainStart?.(chain.fromReel, chain.features);
    }
    dimWaitingReels(chain, reelIndex);
    // The stopped reels' trigger symbols wait — after the reel before this
    // one has started its landing, so its trigger lands first (startWait
    // queues the wait behind a landing still playing).
    if (!tensionDim && waitReel) {
      const prevReel = reelIndex - 1;
      Promise.resolve(prevReel >= 0 ? whenReelLanding?.(prevReel) : null).then(() => {
        if (chainRef.current !== chain || chain.done || chain.waitsEnded) return;
        for (let i = 0; i < reelIndex; i++) waitReel(i);
      });
    }
    // The board frame: up at the chain's first reel, for the whole chain.
    // if (step === 0) getScene()?.playReelAnticipation?.();
    if (firstLive) getScene()?.playReelAnticipation?.();
    // ...and the background darkens with it, as on the Random Pot's
    // presentation spin (Vladimir, 2026-10-22: every anticipation). No-op if
    // already dark (the presentation darkened before its spin).
    if (firstLive) getScene()?.darkenBackground?.();
    // ── The coin counter (GDD "Anticipation Hold & Win": "4 / 6 Coins")
    // ── appears once the coins can still make the trigger: on the first turn
    // after the reel that brought them to START_AT (a scatter chain can start
    // earlier), with the coins down so far. Each chain reel's landing updates
    // it (onLandingPeak); it goes with the chain (endStep).
    const coinCounter = chain.coinCounter;
    if (coinCounter && !chain.coinCounterShown && reelIndex - 1 >= coinCounter.fromReel) {
      chain.coinCounterShown = true;
      // The board fire first, the counter a beat later (COIN_COUNTER_AFTER_FIRE_MS);
      // counts landing meanwhile are kept (onLandingPeak) and shown with it.
      chain.coinCounterCount = coinCounter.counts[reelIndex - 1] ?? 0;
      const showCounter = () => {
        if (chainRef.current !== chain || chain.done) return;
        chain.coinCounterReady = true;
        getScene()?.showCoinCounter?.(chain.coinCounterCount, coinCounter.total);
      };
      if (firstLive) {
        const counterTimer = new TickerTimeout(() => {
          untrack(counterTimer, null);
          showCounter();
        }, COIN_COUNTER_AFTER_FIRE_MS);
        track(counterTimer, null);
      } else {
        showCounter();
      }
    }
    getScene()?.playAnticipation?.(reelIndex);
    // startFireSound();
    scheduleStop(chain, reelIndex, inMs + loopMs * loops);
  };

  // Chain reel `step` is stopping: out in full, then the next chain reel — or,
  // after the last, every reel after the chain at the normal gap. Once per
  // step: the stop calls it, the landing peak is only the fallback.
  function endStep(chain, step) {
    if (chain.endedStep === step) return;
    chain.endedStep = step;
    // A plain turn has no out to play: the next turn starts at its stop.
    const plainStep = !!chain.plain?.includes(chain.reels[step]);
    if (plainStep && step + 1 < chain.reels.length) {
      startStep(chain, step + 1);
      return;
    }
    getScene()?.stopAnticipation?.();
    // stopFireSound();
    // The board frame fades out WITH the last reel's out, not after it.
    if (step + 1 >= chain.reels.length) {
      // getScene()?.stopReelAnticipation?.();
      // // The background lightens with it — the presentation board keeps its
      // // dark to the last reel's landing (onLandingPeak).
      // if (!chain.presentationDark) getScene()?.lightenBackground?.();
      // ── Held to the end of the spin (Vladimir, 2026-10-22) ── the reel's
      // own anticipation ends here as before, but the board fire, the dark
      // and their sounds stay until the LAST reel has landed, then finish
      // with their outs (releaseHeldBoard, from onLandingPeak).
      chain.holdBoard = true;
      // The tension dim lifts (or hands over) right here too — waiting out
      // the whole out left it hanging (Vladimir, 2026-09-29).
      if (tensionDim && cfg.DIM_REELS && !chain.dimLifted) {
        chain.dimLifted = true;
        dimTension(chain, null);
      }
      // Without the tension dim: the waits end here — a hit keeps them for
      // the trigger win, a miss plays them out.
      if (!tensionDim && waitReel && !chain.waitsEnded) {
        chain.waitsEnded = true;
        onChainEnd?.(!!chain.hits);
      }
    }
    const timeoutId = new TickerTimeout(() => {
      untrack(timeoutId, null);
      if (chainRef.current !== chain || chain.done) return;
      if (step + 1 < chain.reels.length) {
        startStep(chain, step + 1);
        return;
      }
      chain.done = true;
      // The coin counter disappears with the chain — a miss per the GDD
      // ("the Coin counter disappears"), and a hit too: the trigger takes over.
      if (chain.coinCounterShown) getScene()?.hideCoinCounter?.();
      // Already lifted at the last reel's stop in tension mode.
      if (!chain.dimLifted) dimWaitingReels(chain, null);
      // Lingered a whole out behind the last reel — now at its stop, above:
      // getScene()?.stopReelAnticipation?.();
      const lastReel = chain.reels[step];
      for (let i = lastReel + 1, n = 0; i < reels().length; i++, n++) {
        scheduleStop(chain, i, normalGapMs * n);
      }
    }, timing().outMs);
    track(timeoutId, null);
  }

  /**
   * Call from every reel's landing PEAK — the overshoot trough, the frame the
   * symbol reads as having arrived. The reel that brings the symbol before
   * the trigger starts the chain. A chain reel's out already started at its
   * stop (stopChainReel); the peak only covers a stop the chain did not make.
   */
  // The held board fire and dark (endStep) finish now: their outs, their
  // sounds fading with them.
  const releaseHeldBoard = (chain) => {
    chain.holdBoard = false;
    getScene()?.stopReelAnticipation?.();
    getScene()?.lightenBackground?.();
  };

  const onLandingPeak = (peakReelIndex) => {
    const chain = chainRef.current;
    // An anticipation's board, held to the spin's end: the last reel landed.
    if (chain?.holdBoard && peakReelIndex >= reels().length - 1) releaseHeldBoard(chain);
    // The Random Pot's presentation fire fades out with its LAST reel's
    // landing — where a natural chain's ends too (endStep).
    if (chain?.presentationFire && peakReelIndex >= reels().length - 1) {
      chain.presentationFire = false;
      getScene()?.stopReelAnticipation?.();
    }
    // ...and its darkened background lightens there too, chain or not.
    if (chain?.presentationDark && peakReelIndex >= reels().length - 1) {
      chain.presentationDark = false;
      getScene()?.lightenBackground?.();
    }
    if (!chain || chain.done) return;
    // A chain reel has landed: the coin counter shows the coins down now.
    if (chain.coinCounterShown && chain.reels?.includes(peakReelIndex)) {
      const { counts, total } = chain.coinCounter;
      // Only the number changes; while it waits for the fire, it is kept.
      chain.coinCounterCount = counts[peakReelIndex] ?? 0;
      if (chain.coinCounterReady) getScene()?.showCoinCounter?.(chain.coinCounterCount, total);
    }
    if (chain.step === -1 && peakReelIndex === chain.fromReel) {
      startStep(chain, 0);
    } else if (chain.step >= 0 && peakReelIndex === chain.reels[chain.step]) {
      endStep(chain, chain.step);
      // The last reel's own landing ended the chain: its board goes now too.
      if (chain.holdBoard && peakReelIndex >= reels().length - 1) releaseHeldBoard(chain);
    }
  };

  /**
   * A press while a chain reel is on its turn: cut its in / loop short and
   * stop it now. The stop plays the out in full and hands on exactly as a
   * natural stop does — so each press skips one reel. A press
   * during the out, or on a reel already stopping, does nothing.
   *
   * @returns {boolean} true when the chain owned the press (the room must not
   *          run its own manual stop), false when no chain reel is on its turn.
   */
  const skipActiveReel = () => {
    if (!isActive()) return false;
    const chain = chainRef.current;
    const activeReel = chain.reels[chain.step];
    const ctrl = reelController(activeReel);
    if (ctrl && !ctrl.hasStopped && !ctrl.isStopping) {
      const pending = chain.stops.get(activeReel);
      if (pending) {
        pending.clear?.();
        chain.stops.delete(activeReel);
        untrack(pending, activeReel);
      }
      stopChainReel(chain, activeReel);
    }
    return true;
  };

  return { sync, holds, isActive, onLandingPeak, skipActiveReel };
};
